import React from 'react';
import { Check } from 'lucide-react';
import { Link } from 'react-router-dom';
import { ServicePageLayout } from '../components/ServicePageLayout';
export function CeramicCoatingPage() {
  const schema = {
    '@context': 'https://schema.org',
    '@type': 'Service',
    name: 'Ceramic Coating',
    provider: {
      '@type': 'LocalBusiness',
      name: "Mike's Pro Detail",
      telephone: '+18183819905'
    },
    areaServed: ['Camarillo', 'Thousand Oaks', 'Ventura County', 'Los Angeles'],
    description: 'Professional ceramic coating application for ultimate paint protection, extreme gloss, and hydrophobic water beading.'
  };
  const faqs = [{
    question: 'How long does a ceramic coating last?',
    answer: 'A properly applied and maintained ceramic coating can last for years, far outlasting traditional wax (weeks) or synthetic sealants (months). Longevity depends on the product tier and how the vehicle is cared for.'
  }, {
    question: 'Does the paint need prep before coating?',
    answer: 'Yes. The paint must be flawless before coating, since the coating locks in any existing defects. Our process includes a thorough wash, iron decontamination, and clay bar treatment before application.'
  }, {
    question: 'What are the benefits of a ceramic coating?',
    answer: 'Ceramic coatings provide extreme hydrophobic water beading, UV and oxidation protection, chemical resistance against bird droppings and sap, and an unmatched candy-like gloss.'
  }, {
    question: 'Can you apply ceramic coating at my home?',
    answer: 'Yes \u2014 our mobile service applies ceramic coatings on-site at your home or office in Ventura County, with the controlled prep and care the process requires.'
  }];
  const breadcrumbs = [{
    name: 'Home',
    item: 'https://www.fulldetailpro.com/'
  }, {
    name: 'Services',
    item: 'https://www.fulldetailpro.com/#services'
  }, {
    name: 'Ceramic Coating',
    item: 'https://www.fulldetailpro.com/services/ceramic-coating'
  }];
  return <ServicePageLayout title="Ceramic Coating Ventura County" subtitle="Experience the ultimate in paint protection. Ceramic coatings provide years of durable protection, extreme gloss, and effortless maintenance." metaDescription="Professional ceramic coating in Ventura County \u2014 Camarillo, Oxnard & Thousand Oaks. Long-lasting paint protection & extreme gloss. Call (818) 381-9905." schema={schema} faqs={faqs} breadcrumbs={breadcrumbs} data-id="element-510">
      <p data-id="element-511">
        If you want the absolute best protection for your vehicle's paint,
        traditional waxes and sealants simply cannot compete with a professional
        . At Mike's Pro Detail, we offer premium hybrid ceramic spray coating
        application that bond at a molecular level with your vehicle's clear
        coat. This creates a semi-permanent, glass-like shield that protects
        against the harsh elements of Southern California. Whether you're
        driving through Thousand Oaks or parked under the sun in Camarillo CA,
        our mobile ceramic coating service ensures your vehicle remains
        protected and incredibly glossy for years.
      </p>

      <h2 data-id="element-512">What is a Ceramic Coating?</h2>
      <p data-id="element-513">
        A hybrid ceramic coating is a liquid polymer (typically silica-based)
        that is carefully applied with spray to the exterior of a vehicle. Once
        cured, it chemically bonds with the factory paint, creating a rigid,
        hydrophobic (water-repelling) layer of protection. Unlike carnauba wax
        that melts away in a few weeks, or synthetic sealants that last a few
        months, a hybrid ceramic coating can last for 1 to 2 years when properly
        maintained.
      </p>

      <h2 data-id="element-514">The Benefits of Ceramic Coating</h2>

      <ul className="space-y-4 my-6 list-none pl-0" data-id="element-515">
        <li className="flex items-start" data-id="element-516">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-517" />
          <span data-id="element-518">
            <strong data-id="element-519">Extreme Hydrophobic Properties:</strong> Water aggressively
            beads up and rolls off the surface, taking dirt and grime with it.
            This self-cleaning effect makes washing your car incredibly easy.
          </span>
        </li>
        <li className="flex items-start" data-id="element-520">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-521" />
          <span data-id="element-522">
            <strong data-id="element-523">UV Damage & Oxidation Prevention:</strong> The coating
            blocks harmful UV rays, preventing your paint from oxidizing,
            fading, and looking dull over time.
          </span>
        </li>
        <li className="flex items-start" data-id="element-524">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-525" />
          <span data-id="element-526">
            <strong data-id="element-527">Chemical Resistance:</strong> Protects against acidic bird
            droppings, tree sap, bug splatter, and harsh chemicals that would
            normally etch into the clear coat.
          </span>
        </li>
        <li className="flex items-start" data-id="element-528">
          <Check className="w-6 h-6 text-red-500 mr-3 shrink-0 mt-1" data-id="element-529" />
          <span data-id="element-530">
            <strong data-id="element-531">Unmatched Candy-Like Gloss:</strong> Enhances the reflective
            properties of your paint, adding depth and clarity that makes your
            car look like it's encased in glass.
          </span>
        </li>
      </ul>

      <h2 data-id="element-532">The Application Process</h2>
      <p data-id="element-533">
        Applying a hybrid ceramic coating requires preparation. The hybrid
        ceramic coating will lock in any existing defects. Our process includes
        a thorough wash, chemical decontamination, and clay bar treatment.
      </p>

      <h2 data-id="element-534">Why Choose Us for Ceramic Coatings?</h2>
      <p data-id="element-535">
        Applying a ceramic coating is a highly technical process that requires a
        controlled environment, patience, and expertise. When searching for "Car
        Detailing Camarillo" or "Mobile Detailing Ventura County" for high-end
        protection, Mike's Pro Detail is the premier choice. We use only
        industry-leading, professional-grade coatings and take the necessary
        time to prep your vehicle correctly. Protect your investment with the
        ultimate shield—contact us today to discuss the best ceramic coating
        package for your vehicle.
      </p>

      <h2 data-id="element-536">Related Detailing Services</h2>
      <div className="not-prose grid grid-cols-1 sm:grid-cols-2 gap-3 my-6" data-id="element-537">
        <Link to="/services/exterior-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-538">
          Exterior Detailing &rarr;
        </Link>
        <Link to="/services/full-car-detail" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-539">
          Full Car Detail &rarr;
        </Link>
        <Link to="/services/mobile-detailing" className="block px-4 py-3 bg-zinc-900/60 border border-zinc-800 rounded-lg text-white font-bold hover:border-red-900/60 hover:bg-zinc-800/60 transition-all" data-id="element-540">
          Mobile Detailing &rarr;
        </Link>
      </div>
    </ServicePageLayout>;
}